import { createContext, useContext } from 'react';
import { TimeRange } from "../types/TimeRange";
import * as dayjs from 'dayjs';
import * as QueryString from "query-string";
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import { PERIOD_CUSTOM, PERIOD_ONE_HOUR, periodToValidIntervals } from "../component/Constants";

dayjs.extend(utc.default);
dayjs.extend(timezone.default);
dayjs.extend(customParseFormat.default);

// Check for QueryString parameters.
let queryString = QueryString.parse(window.location.search);

// Etc/UTC and UTC are equivalent IANA timezones - normalize for comparison
const normalizeTz = (tz: string) => (tz === 'Etc/UTC' ? 'UTC' : tz);

// Set the internal context object.
const savedTimeZone = window.localStorage.getItem('dbmar-timeZone')
const rawTimeZone = savedTimeZone ? JSON.parse(savedTimeZone) : Intl.DateTimeFormat().resolvedOptions().timeZone
const timeZoneValue = typeof rawTimeZone === 'string' ? rawTimeZone : (Array.isArray(rawTimeZone) ? rawTimeZone[0] : 'UTC')
const timeNow = timeZoneValue === 'UTC' ? dayjs.utc() : dayjs.default().tz(timeZoneValue)
const savedInterval = JSON.parse(window.localStorage.getItem('dbmar-interval') || '0')
const validInterval = periodToValidIntervals.hours_1.includes(savedInterval)

let timeRange: TimeRange = {
    interval: validInterval ? savedInterval : 0,
    timezone: normalizeTz(timeZoneValue || Intl.DateTimeFormat().resolvedOptions().timeZone),
    previousFrom: timeNow.subtract(2, 'hour').second(0),
    from: timeNow.subtract(1, 'hour').second(0),
    to: timeNow.second(0),
    dateFormat: 'YYYY-MM-DD+HH:mm:00',
    period: PERIOD_ONE_HOUR,
    autorefresh: false
};

// Normalize time strings: URLs may use HH:mm (no seconds), or space instead of + - normalize for parsing
const parseTimeParam = (s: string) => {
    const str = s.toString().trim().replace(/^(\d{4}-\d{2}-\d{2})\s(\d{2}:\d{2})$/, '$1+$2');
    return /^\d{4}-\d{2}-\d{2}\+\d{2}:\d{2}$/.test(str) ? `${str}:00` : str;
};

const parseUrlTime = (str: string, tz: string) => {
    const normalized = parseTimeParam(str);
    const parsed = dayjs.tz(normalized, 'YYYY-MM-DD+HH:mm:ss', tz);
    return parsed.isValid() ? parsed : null;
};

if (queryString.fm && queryString.to && queryString.tz) {
    const timezone = (typeof queryString.tz === 'object' ? queryString.tz[0] : queryString.tz) || 'UTC'
    const intervalParam = (typeof queryString.interval === 'object' ? queryString.interval?.[0] : queryString.interval) || '0'
    const fromTime = parseUrlTime(queryString.fm.toString(), timezone);
    const toTime = parseUrlTime(queryString.to.toString(), timezone);

    if (fromTime && toTime) {
        // Use URL values for display - do NOT persist to localStorage (link should not override user's saved preferences)
        timeRange.from = fromTime;
        timeRange.to = toTime;
        timeRange.previousFrom = timeRange.from.subtract(timeRange.to.diff(timeRange.from, 'millisecond'), 'millisecond');
        timeRange.period = PERIOD_CUSTOM
        const intervalURLvalue = parseInt(String(intervalParam), 10) || 0;
        timeRange.interval = intervalURLvalue;
        const tzParam = typeof queryString.tz === 'string' ? queryString.tz : (Array.isArray(queryString.tz) ? queryString.tz[0] : timeZoneValue);
        timeRange.timezone = normalizeTz(tzParam || timeZoneValue);
    }
}

export type SetTimeRangeParamType = Pick<TimeRange, 'timezone' | 'from' | 'to' | 'interval' | 'period' | 'autorefresh'>

/** If URL has fm/to/tz that differ from user's timezone, return new search string with times converted to user's tz. Otherwise null (no redirect needed). */
export function getConvertedTimeRangeRedirectSearch(search: string): string | null {
    const parsed = QueryString.parse(search);
    if (!parsed.fm || !parsed.to || !parsed.tz) return null;
    const urlTz = (typeof parsed.tz === 'object' ? parsed.tz[0] : parsed.tz) || 'UTC';
    let userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    try {
        const saved = window.localStorage.getItem('dbmar-timeZone');
        if (saved) {
            const raw = JSON.parse(saved);
            userTz = typeof raw === 'string' ? raw : (Array.isArray(raw) ? raw[0] : userTz);
        }
    } catch {
        /* keep default */
    }
    const userTzNorm = normalizeTz(userTz);
    const urlTzNorm = normalizeTz(urlTz);
    if (userTzNorm === urlTzNorm) return null;
    const fromTime = parseUrlTime(parsed.fm.toString(), urlTz);
    const toTime = parseUrlTime(parsed.to.toString(), urlTz);
    if (!fromTime || !toTime) return null;
    const fromConverted = fromTime.tz(userTzNorm).format('YYYY-MM-DD+HH:mm:ss');
    const toConverted = toTime.tz(userTzNorm).format('YYYY-MM-DD+HH:mm:ss');
    const rest = { ...parsed };
    delete rest.fm;
    delete rest.to;
    delete rest.tz;
    const base = `fm=${fromConverted}&to=${toConverted}&tz=${userTzNorm}`;
    const restStr = QueryString.stringify(rest);
    return restStr ? `${base}&${restStr}` : base;
}

/** Parse fm, to, tz (and optional interval) from URL search string. Used when navigating in-app to sync context from URL. */
export function parseTimeRangeFromSearchParams(search: string): SetTimeRangeParamType | null {
    const parsed = QueryString.parse(search);
    if (!parsed.fm || !parsed.to || !parsed.tz) return null;
    const tzParam = (typeof parsed.tz === 'object' ? parsed.tz[0] : parsed.tz) || 'UTC';
    const intervalParam = (typeof parsed.interval === 'object' ? parsed.interval?.[0] : parsed.interval) || '0';
    const fromTime = parseUrlTime(parsed.fm.toString(), tzParam);
    const toTime = parseUrlTime(parsed.to.toString(), tzParam);
    if (!fromTime || !toTime) return null;
    const tzVal = typeof parsed.tz === 'string' ? parsed.tz : (Array.isArray(parsed.tz) ? parsed.tz[0] : tzParam);
    const intervalURLvalue = parseInt(String(intervalParam), 10) || 0;
    return {
        from: fromTime,
        to: toTime,
        timezone: normalizeTz(tzVal || 'UTC'),
        interval: intervalURLvalue,
        period: PERIOD_CUSTOM,
        autorefresh: false
    };
}

// Define the context type, including the update function.
export type TimeRangeContextType = {
    timeRange: TimeRange;
    setTimeRange: (timeRange: SetTimeRangeParamType) => void;
    getTimeRangeQueryString: (previous?: boolean, interval?: number) => string,
    getTimeRangeRedirectString: () => string
}

// Define the context object.
export const TimeRangeContext = createContext<TimeRangeContextType>({
    timeRange: timeRange,
    getTimeRangeQueryString: (previous = false) => {
        let from, to;
        from = previous
                ? timeRange.previousFrom.format('YYYY-MM-DD+HH:mm:ss')
                : timeRange.from.format('YYYY-MM-DD+HH:mm:ss');

        to = previous
                ? timeRange.from.format('YYYY-MM-DD+HH:mm:ss')
                : timeRange.to.format('YYYY-MM-DD+HH:mm:ss');

        return `from=${from}&to=${to}&tz=${timeRange.timezone}&interval=${timeRange.interval || 0}`;
    },

    getTimeRangeRedirectString: () => {
        const fm = timeRange.from.format('YYYY-MM-DD+HH:mm:ss');
        const to = timeRange.to.format('YYYY-MM-DD+HH:mm:ss');
        return `fm=${fm}&to=${to}&tz=${timeRange.timezone}&interval=${timeRange.interval}`;
    },
    setTimeRange: (newTimeRange: SetTimeRangeParamType) => {
        const from = newTimeRange.from
        const to = newTimeRange.to
        timeRange.previousFrom = from.subtract(newTimeRange.to.diff(from, 'millisecond'), 'millisecond');
        timeRange.from = from;
        timeRange.to = to;
        timeRange.timezone = normalizeTz(newTimeRange.timezone);
        timeRange.interval = newTimeRange.interval;
        timeRange.period = newTimeRange.period;
        timeRange.autorefresh = newTimeRange.autorefresh;
    }
});
TimeRangeContext.displayName = 'TimeRange';

// Export the update function for the context object.
export const useTimeRange = () => useContext(TimeRangeContext);
